// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import xLoadingOverlay from "@west/App/Handlers/XNG/LoadingOverlay";
import {
    sendVerifyChangeEmail,
    VerifyChangeEmailApiParameter,
    VerifyChangeEmailModel,
} from "@west/App/Services/Auth0Service";

type Parameter = VerifyChangeEmailApiParameter &
    HasOnError<VerifyChangeEmailModel> &
    HasOnSuccess<VerifyChangeEmailModel>;

type HandlerType = ApiHandlerDiscreteParameter<Parameter, VerifyChangeEmailApiParameter, VerifyChangeEmailModel>;

const LOADING_ID = "verifyChangeEmail";

export const ValidateParameter: HandlerType = props => {
    const { idToken, state } = props.parameter;
    if (!idToken || !state) {
        return false;
    }
};

export const DispatchBeginVerifyChangeEmail: HandlerType = props => {
    props.dispatch(
        xLoadingOverlay({
            key: "add",
            value: LOADING_ID,
        }),
    );
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        idToken: props.parameter.idToken,
        state: props.parameter.state,
    };
};

export const SendVerifyChangeEmail: HandlerType = async props => {
    try {
        props.apiResult = await sendVerifyChangeEmail(props.apiParameter);
    } catch (error) {
        props.apiResult = {
            valid: false,
        };
    }
};

export const DispatchCompleteVerifyChangeEmail: HandlerType = props => {
    props.dispatch(
        xLoadingOverlay({
            key: "remove",
            value: LOADING_ID,
        }),
    );
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.valid) {
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.valid) {
        props.parameter.onError?.(props.apiResult);
    }
};

const auth0VerifyChangeEmail = createHandlerChainRunner(
    [
        ValidateParameter,
        DispatchBeginVerifyChangeEmail,
        PopulateApiParameter,
        SendVerifyChangeEmail,
        DispatchCompleteVerifyChangeEmail,
        ExecuteOnSuccessCallback,
        ExecuteOnErrorCallback,
    ],
    "Auth0VerifyChangeEmail",
);

export default auth0VerifyChangeEmail;
